// Encode/decode HotChocolate `[UsePaging]` IQueryable cursors.
//
// The default HotChocolate cursor is the base64 of the row's 0-based index as
// a decimal string — e.g. index 3 -> base64("3") -> "Mw==". This is verified
// against a live HotChocolate schema by the FR-10 guard test
// (test/.../GraphQLQueryTests/CursorFormatGuardTests.cs).
//
// Because the cursor is a positional index, jump-to-page derives the `after`
// cursor for an arbitrary page purely from page math — no need to walk the
// connection. If a future HotChocolate upgrade changes this encoding, the
// guard test (and cursorCodec.test.js) fail loudly so the breakage surfaces
// immediately instead of silently corrupting jump-to-page.
//
// NOTE: this is the cursor for the standard relay connections. A separate
// bespoke keyset cursor (WebApi GraphQL/Models/Cursor.cs — LastId/Count/...)
// is used by a few manual-pagination queries and is NOT handled here; those
// are not driven by <DataTable>.

const decodeBase64 = (value) =>
  typeof atob === "function"
    ? atob(value)
    : Buffer.from(value, "base64").toString("binary");

const encodeBase64 = (value) =>
  typeof btoa === "function"
    ? btoa(value)
    : Buffer.from(value, "binary").toString("base64");

/** 0-based row index a cursor points at, or null if it can't be decoded. */
export const positionFromCursor = (cursor) => {
  if (!cursor) return null;
  const parsed = parseInt(decodeBase64(cursor), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Encode a 0-based row index as a cursor (base64 of the decimal string). */
export const encodeCursorForPosition = (position) =>
  encodeBase64(String(position));

/**
 * Build the `after` cursor that lands a connection on (0-based) `pageIndex`
 * at `pageSize`. Returns null for the first page (no `after` argument).
 *
 * The `after` cursor must point at the item *before* the first row of the
 * target page, so the connection returns the page starting at that row.
 */
export const encodeAfterCursor = (pageIndex, pageSize) => {
  const position = pageIndex * pageSize - 1;
  if (position < 0) return null;
  return encodeCursorForPosition(position);
};
